import type { ReactNode } from "react";
import { useRef } from "react";
import { motion, useMotionValue, useReducedMotion, useSpring } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { fadeUp, inView, stagger } from "../lib/motion";

type DivProps = { children: ReactNode; className?: string };

export function Section({
  id,
  children,
  className = "",
}: DivProps & { id?: string }) {
  return (
    <section
      id={id}
      className={`relative mx-auto w-full max-w-6xl px-5 py-24 sm:px-8 md:py-32 ${className}`}
    >
      {children}
    </section>
  );
}

/** Small label above a headline. */
export function Eyebrow({ children, tone = "green" }: DivProps & { tone?: "green" | "blue" | "gold" }) {
  const dot = tone === "blue" ? "bg-blue" : tone === "gold" ? "bg-gold" : "bg-green";
  return (
    <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-ink-muted">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {children}
    </span>
  );
}

export function SectionHead({
  eyebrow,
  title,
  copy,
  tone = "green",
}: {
  eyebrow: string;
  title: ReactNode;
  copy?: ReactNode;
  tone?: "green" | "blue" | "gold";
}) {
  return (
    <motion.div
      variants={stagger}
      initial="hidden"
      whileInView="show"
      viewport={inView}
      className="max-w-2xl"
    >
      <motion.div variants={fadeUp}>
        <Eyebrow tone={tone}>{eyebrow}</Eyebrow>
      </motion.div>
      <motion.h2
        variants={fadeUp}
        className="mt-4 text-3xl font-extrabold leading-[1.08] sm:text-4xl md:text-5xl"
      >
        {title}
      </motion.h2>
      {copy && (
        <motion.p variants={fadeUp} className="mt-5 text-base leading-relaxed text-ink-soft sm:text-lg">
          {copy}
        </motion.p>
      )}
    </motion.div>
  );
}

export function Card({ children, className = "" }: DivProps) {
  return (
    <div
      className={`ring-soft rounded-2xl border border-line/70 bg-surface/80 ${className}`}
    >
      {children}
    </div>
  );
}

export function Pill({ children, className = "" }: DivProps) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border border-line bg-elevated/70 px-3 py-1 text-xs font-medium text-ink-soft ${className}`}
    >
      {children}
    </span>
  );
}

export function VerifiedBadge({ className = "" }: { className?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border border-green/40 bg-green/10 px-2.5 py-1 text-[11px] font-semibold text-green ${className}`}
    >
      <ShieldCheck className="h-3.5 w-3.5" /> Verified
    </span>
  );
}

type ButtonProps = {
  children: ReactNode;
  href?: string;
  variant?: "primary" | "ghost";
  className?: string;
  type?: "button" | "submit";
  disabled?: boolean;
};

export function Button({
  children,
  href,
  variant = "primary",
  className = "",
  type = "button",
  disabled = false,
}: ButtonProps) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-60";
  const styles =
    variant === "primary"
      ? "bg-green text-background hover:brightness-110 hover:shadow-[0_16px_40px_-16px] hover:shadow-green/70 active:scale-[0.98]"
      : "border border-line bg-elevated/40 text-ink hover:border-ink-muted/60 hover:bg-elevated active:scale-[0.98]";
  const cls = `${base} ${styles} ${className}`;
  if (href) {
    return (
      <a href={href} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <button type={type} className={cls} disabled={disabled}>
      {children}
    </button>
  );
}

/**
 * Magnetic CTA — on a fine pointer (desktop) the button eases toward the cursor;
 * on touch / reduced-motion it behaves like a normal link with no transform.
 */
export function MagneticButton({
  children,
  href,
  className = "",
  strength = 0.4,
}: {
  children: ReactNode;
  href: string;
  className?: string;
  strength?: number;
}) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLAnchorElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 15, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 200, damping: 15, mass: 0.4 });

  function onMove(e: React.PointerEvent) {
    if (reduce || e.pointerType !== "mouse" || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * strength);
    y.set((e.clientY - (r.top + r.height / 2)) * strength);
  }
  function reset() {
    x.set(0);
    y.set(0);
  }

  return (
    <motion.a
      ref={ref}
      href={href}
      onPointerMove={onMove}
      onPointerLeave={reset}
      style={reduce ? undefined : { x: sx, y: sy }}
      className={`inline-flex items-center justify-center gap-2 rounded-full bg-green px-6 py-3 text-sm font-semibold text-background transition-[filter,box-shadow] duration-200 hover:brightness-110 hover:shadow-[0_18px_44px_-16px] hover:shadow-green/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-green/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background ${className}`}
    >
      {children}
    </motion.a>
  );
}

/** Circular score ring with an animated sweep on view. */
export function ScoreRing({
  value,
  size = 132,
  stroke = 10,
  label = "DNA Score",
}: {
  value: number;
  size?: number;
  stroke?: number;
  label?: string;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value)) / 100;
  return (
    <div className="relative grid place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-line)" strokeWidth={stroke} />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--color-green)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: c - c * pct }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          style={{ filter: "drop-shadow(0 0 6px color-mix(in srgb, var(--color-green) 60%, transparent))" }}
        />
      </svg>
      {/* Center label is sized + clamped to stay inside the ring, never overlap it. */}
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="font-extrabold tracking-tight" style={{ fontSize: Math.round(size * 0.3) }}>
          {value}
        </span>
        <span
          className="mt-1 whitespace-nowrap text-center font-medium uppercase tracking-wider text-ink-muted"
          style={{ fontSize: Math.max(8, Math.round(size * 0.072)), maxWidth: size * 0.78 }}
        >
          {label}
        </span>
      </div>
    </div>
  );
}

/** Inline labelled meter used in score / coach cards. */
export function Meter({ label, value, color = "var(--color-green)" }: { label: string; value: number; color?: string }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-ink-soft">{label}</span>
        <span className="font-semibold text-ink">{value}</span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-line/70">
        <motion.div
          className="h-full rounded-full"
          style={{ background: color }}
          initial={{ width: 0 }}
          whileInView={{ width: `${value}%` }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    </div>
  );
}
